import { RoutineWorkflow, useCurrentWorkspace, useEntityAlerts } from '@gripp/shared-logic';
import {
  AddButton,
  AlertSummary,
  ColumnData,
  LoadingIndicator,
  PanelShell,
  SlideAction,
  tokens
} from '@gripp/shared-ui';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import {
  GroupEditFormContent,
  GroupSelectionPanelHeader,
} from './groupsSelectionPanel';

// ── Alert list content ─────────────────────────────────────────────────────────
// Exported so it can be re-used as the left column in group-alert-form-page.tsx.

type GroupAlertListContentProps = {
  groupId: string;
  /** The alert currently open in the form column, if any. */
  selectedAlertId?: string;
  /** True when the new-alert form is open in the form column. */
  isCreatingNew?: boolean;
};

export const GroupAlertListContent = ({
  groupId,
  selectedAlertId,
  isCreatingNew = false,
}: GroupAlertListContentProps) => {
  const navigate = useNavigate();
  const { workflows, isLoading } = useEntityAlerts('group', groupId);

  const handleAlertSelected = (selectedAlert: RoutineWorkflow) => {
    if (!selectedAlert?.id) {
      return;
    }

    // When the right column is already showing an alert form (edit or new),
    // navigate relative to the alerts list root so we replace the current form
    // rather than nesting deeper.
    const isOnAlertForm = !!selectedAlertId || isCreatingNew;
    navigate(
      isOnAlertForm ? `../${selectedAlert.id}` : selectedAlert.id,
      { relative: 'path' }
    );
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingIndicator flexContainer />
      </View>
    );
  }

  return (
    <View style={styles.listContainer}>
      <ScrollView>
        {workflows.map((item: RoutineWorkflow) => (
          <AlertSummary
            key={item.id}
            workflow={item}
            routineId={item.trigger?.config?.workspaceRoutineId ?? ''}
            onAlertSelected={handleAlertSelected}
          />
        ))}
      </ScrollView>
    </View>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────────

export const GroupAlerts = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const currentWorkspace = useCurrentWorkspace();

  if (!groupId) return null;

  const leftColumn: ColumnData = {
    title: 'groups.menu.editGroup',
    mainComponent: currentWorkspace ? (
      <GroupEditFormContent
        groupId={groupId}
        workspaceId={currentWorkspace}
        onComplete={() => navigate('../..', { relative: 'path' })}
      />
    ) : (
      <View style={styles.loadingContainer}>
        <LoadingIndicator flexContainer />
      </View>
    ),
    headerComponent: currentWorkspace ? (
      <GroupSelectionPanelHeader groupId={groupId} />
    ) : undefined,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => navigate('../..', { relative: 'path' }),
    disableScroll: true,
  };

  const rightColumn: ColumnData = {
    title: 'alerts.alerts',
    mainComponent: <GroupAlertListContent groupId={groupId} />,
    headerComponent: (
      <AddButton
        variant="header"
        onPress={() => navigate('new', { relative: 'path' })}
        accessibilityLabel="Create Alert"
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => navigate('../..', { relative: 'path' }),
    disableScroll: true,
  };

  return (
    <PanelShell
      leftColumn={leftColumn}
      rightColumnOverride={rightColumn}
    />
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  listContainer: {
    flex: 1,
    margin: tokens.spacing.xl,
  },
  loadingContainer: {
    margin: tokens.spacing.xl,
  },
});
